import { useRef, useState, type ComponentType, type KeyboardEvent } from 'react'
import { Check, CheckCircle, type IconProps } from '@phosphor-icons/react'
import type { DialogLine, DialogResult } from './content'

export interface DialogItem {
  key: string
  label: string
  icon: ComponentType<IconProps>
  /** Who is calling where and when, shown above the transcript. */
  context?: string
  dialog: DialogLine[]
  result: DialogResult
  handles?: string[]
  /** A recording of this very conversation. Without one the transcript
   * stands alone; the player never shows up empty. */
  audio?: { src: string; duration: string }
}

/** A sample call next to what it left behind (the booking, the lead). */
export function DialogPanel({
  dialog,
  result,
  handles,
  context,
  audio,
  id,
  labelledBy,
}: Pick<DialogItem, 'dialog' | 'result' | 'handles' | 'context' | 'audio'> & { id?: string; labelledBy?: string }) {
  const tabbed = Boolean(labelledBy)
  return (
    <div
      className="lp-ind-panel"
      id={id}
      role={tabbed ? 'tabpanel' : undefined}
      aria-labelledby={labelledBy}
    >
      <figure className="lp-ind-dialog">
        <figcaption>{context ?? 'Приклад розмови'}</figcaption>
        {audio && (
          <div className="lp-audio">
            <audio controls preload="none" src={audio.src}>
              <a href={audio.src}>Завантажити запис</a>
            </audio>
            <span>Запис розмови, {audio.duration}</span>
          </div>
        )}
        <ol>
          {dialog.map((line, i) => (
            <li key={i} className={`lp-bubble is-${line.who}`}>
              <span>{line.who === 'ai' ? 'AI-агент' : 'Клієнт'}</span>
              <p>{line.text}</p>
            </li>
          ))}
        </ol>
      </figure>

      <div className="lp-ind-side">
        <div className="lp-ind-result">
          <p className="lp-ind-result-title">
            <CheckCircle size={20} weight="fill" /> {result.title}
          </p>
          <dl>
            {result.rows.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          <p className="lp-ind-dest">{result.destination}</p>
        </div>
        {handles && (
          <ul className="lp-ind-handles">
            {handles.map((h) => (
              <li key={h}>
                <Check size={16} weight="bold" aria-hidden="true" />
                {h}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/** Several sample calls, one tab each. ARIA tabs with arrow-key focus. */
export function DialogTabs({ items, label }: { items: DialogItem[]; label: string }) {
  const [active, setActive] = useState(0)
  const tabs = useRef<(HTMLButtonElement | null)[]>([])
  const item = items[active]

  function onKey(e: KeyboardEvent) {
    const last = items.length - 1
    let next = active
    if (e.key === 'ArrowRight') next = active === last ? 0 : active + 1
    else if (e.key === 'ArrowLeft') next = active === 0 ? last : active - 1
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = last
    else return
    e.preventDefault()
    setActive(next)
    tabs.current[next]?.focus()
  }

  return (
    <div className="lp-ind">
      <div className="lp-ind-tabs" role="tablist" aria-label={label} onKeyDown={onKey}>
        {items.map((it, i) => {
          const Icon = it.icon
          const selected = i === active
          return (
            <button
              key={it.key}
              ref={(el) => { tabs.current[i] = el }}
              type="button"
              role="tab"
              id={`lp-tab-${it.key}`}
              aria-selected={selected}
              aria-controls={`lp-panel-${it.key}`}
              tabIndex={selected ? 0 : -1}
              className={selected ? 'is-on' : undefined}
              onClick={() => setActive(i)}
            >
              <Icon size={18} weight={selected ? 'fill' : 'regular'} />
              {it.label}
            </button>
          )
        })}
      </div>
      <DialogPanel
        key={item.key}
        id={`lp-panel-${item.key}`}
        labelledBy={`lp-tab-${item.key}`}
        dialog={item.dialog}
        result={item.result}
        handles={item.handles}
        context={item.context}
        audio={item.audio}
      />
    </div>
  )
}
